import { FileSyncStatus, SyncOperation } from '../common/models/enums';

// Type aliases for API responses (enum values as strings)
type FileSyncStatusString = FileSyncStatus;
type SyncOperationString = SyncOperation;

interface DesktopMessage {
  type: string;
  payload: unknown;
  id: string;
}

interface DesktopResponse {
  id: string;
  success: boolean;
  data?: unknown;
  error?: string;
}

type ConnectionState = 'unknown' | 'connected' | 'disconnected';

// The Electron renderer gets the per-launch bridge secret from preload; plain
// browsers have none, so the desktop bridge rejects them.
async function bridgeHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  try {
    const token = await window.electronAPI?.getBridgeToken?.();
    if (token) headers['X-Bridge-Token'] = token;
  } catch {
    // No token: the request is sent unauthenticated and the bridge rejects it.
  }
  return headers;
}

export class DesktopBridge {
  private static RETRY_COOLDOWN_MS = 30_000;
  private connectionState: ConnectionState = 'unknown';
  private lastAttemptMs = 0;
  private isInitialized = false;
  private messageQueue: Map<
    string,
    { resolve: (value: unknown) => void; reject: (error: unknown) => void }
  > = new Map();
  private readonly boundMessageHandler = (event: MessageEvent) =>
    this.handleMessage(event);
  private readonly boundFocusHandler = () => {
    if (this.connectionState === 'disconnected' && this.canRetry()) {
      void this.checkDesktopAvailability();
    }
  };

  constructor(
    options: boolean | { initialize?: boolean; probe?: boolean } = true
  ) {
    const initialize =
      typeof options === 'boolean' ? options : options.initialize !== false;
    const probe = typeof options === 'boolean' ? true : options.probe !== false;
    if (initialize) this.init(probe);
  }

  private init(probe = true) {
    if (typeof window === 'undefined' || this.isInitialized) return;

    window.addEventListener('message', this.boundMessageHandler);

    // Re-check when the tab regains focus, but only when the bridge is already
    // known-disconnected and the retry cooldown has elapsed.
    window.addEventListener('focus', this.boundFocusHandler);
    this.isInitialized = true;

    // Initial silent probe to discover connection state on page load.
    if (probe) void this.checkDesktopAvailability();
  }

  private handleMessage(event: MessageEvent) {
    if (typeof window === 'undefined' || event.source !== window) return;

    const message = event.data as DesktopResponse;
    if (message && message.id && this.messageQueue.has(message.id)) {
      const { resolve, reject } = this.messageQueue.get(message.id)!;
      this.messageQueue.delete(message.id);

      if (message.success) {
        resolve(message.data);
      } else {
        reject(new Error(message.error));
      }
    }
  }

  private async checkDesktopAvailability() {
    await this.tryConnect();
  }

  private async sendMessage(type: string, payload: unknown): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window not available'));
        return;
      }

      const id = Math.random().toString(36).substr(2, 9);

      this.messageQueue.set(id, { resolve, reject });

      const message: DesktopMessage = {
        type,
        payload,
        id,
      };

      // Skip the HTTP probe while the bridge is known-disconnected and the
      // retry cooldown has not yet elapsed, to prevent repeated
      // ERR_CONNECTION_REFUSED entries in the browser console.
      if (this.connectionState === 'disconnected' && !this.canRetry()) {
        this.messageQueue.delete(id);
        reject(new Error('Desktop app not connected'));
        return;
      }
      this.lastAttemptMs = Date.now();

      // Try HTTP bridge first (for web app connecting to separate Electron app)
      bridgeHeaders()
        .then((headers) =>
          fetch('http://localhost:8765/bridge', {
            method: 'POST',
            headers,
            body: JSON.stringify(message),
          })
        )
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          this.connectionState = 'connected';
          if (this.messageQueue.has(id)) {
            const { resolve: resolveFn, reject: rejectFn } =
              this.messageQueue.get(id)!;
            this.messageQueue.delete(id);

            if (data.success) {
              // HTTP response has data nested, extract it
              resolveFn(data.data);
            } else {
              rejectFn(new Error(data.error || 'Unknown error'));
            }
          }
        })
        .catch((fetchError) => {
          // Fallback to postMessage (for when web app is running inside Electron)
          // Only use postMessage if HTTP completely failed (not just a bad response)
          if (
            fetchError.message &&
            fetchError.message.includes('Failed to fetch')
          ) {
            this.connectionState = 'disconnected';
            // HTTP server not available, try postMessage
            window.postMessage(message, '*');

            // Timeout after 5 seconds
            setTimeout(() => {
              if (this.messageQueue.has(id)) {
                this.messageQueue.delete(id);
                reject(new Error('Desktop app not responding'));
              }
            }, 5000);
          } else {
            // HTTP request succeeded but returned an error
            if (this.messageQueue.has(id)) {
              const { reject: rejectFn } = this.messageQueue.get(id)!;
              this.messageQueue.delete(id);
              rejectFn(fetchError);
            }
          }
        });
    });
  }

  public getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  public canRetry(): boolean {
    if (this.connectionState !== 'disconnected') return true;
    return Date.now() - this.lastAttemptMs >= DesktopBridge.RETRY_COOLDOWN_MS;
  }

  /**
   * Make one explicit connection attempt.  Updates the cached connection state
   * and returns true if the bridge responded successfully.
   */
  public async tryConnect(): Promise<boolean> {
    this.lastAttemptMs = Date.now();
    const wasConnected = this.connectionState === 'connected';
    try {
      const id = Math.random().toString(36).substr(2, 9);
      const response = await fetch('http://localhost:8765/bridge', {
        method: 'POST',
        headers: await bridgeHeaders(),
        body: JSON.stringify({ type: 'ping', payload: {}, id }),
      });
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
      this.connectionState = 'connected';
      if (!wasConnected) {
        console.log('🖥️ Desktop app connected');
      }
      return true;
    } catch {
      this.connectionState = 'disconnected';
      return false;
    }
  }

  /**
   * Returns the cached connection state without making a network probe.
   * Use `tryConnect()` for an explicit check.
   */
  public async isDesktopAvailable(): Promise<boolean> {
    if (this.connectionState === 'connected') return true;
    if (this.connectionState === 'disconnected' && !this.canRetry())
      return false;
    return this.tryConnect();
  }

  public async selectFolder(): Promise<string | null> {
    try {
      const result = await this.sendMessage('select-folder', {});
      return result as string | null;
    } catch (error) {
      console.error('Failed to select folder via desktop:', error);
      return null;
    }
  }

  public async readFile(filePath: string): Promise<{
    success: boolean;
    content?: string;
    isBinary?: boolean;
    error?: string;
  }> {
    try {
      const result = await this.sendMessage('read-file', { filePath });
      // The result from sendMessage is the data object
      const data = result as {
        success?: boolean;
        content?: string;
        isBinary?: boolean;
        error?: string;
      };
      return {
        success: data.success ?? true,
        content: data.content,
        isBinary: data.isBinary,
        error: data.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public async writeFile(
    filePath: string,
    content: string,
    encoding?: 'base64' | 'utf8'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.sendMessage('write-file', {
        filePath,
        content,
        encoding,
      });
      // The result from sendMessage is the data object
      const data = result as { success?: boolean; error?: string };
      return {
        success: data.success ?? true,
        error: data.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public async readDirectory(dirPath: string): Promise<{
    success: boolean;
    items?: Array<{ name: string; isDirectory: boolean; isFile: boolean }>;
    error?: string;
  }> {
    try {
      const result = await this.sendMessage('read-directory', { dirPath });
      // The result from sendMessage is the data object
      const data = result as {
        success?: boolean;
        items?: Array<{ name: string; isDirectory: boolean; isFile: boolean }>;
        error?: string;
      };
      return {
        success: data.success ?? true,
        items: data.items,
        error: data.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public async getFileInfo(filePath: string): Promise<{
    success: boolean;
    info?: {
      size: number;
      modified: Date;
      created: Date;
      isDirectory: boolean;
      isFile: boolean;
    };
    error?: string;
  }> {
    try {
      const result = await this.sendMessage('get-file-info', { filePath });
      // The result from sendMessage is the data object
      const data = result as {
        success?: boolean;
        info?: {
          size: number;
          modified: Date;
          created: Date;
          isDirectory: boolean;
          isFile: boolean;
        };
        error?: string;
      };
      return {
        success: data.success ?? true,
        info: data.info,
        error: data.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public async getSyncStatus(projectId: string): Promise<{
    success: boolean;
    fileStatuses?: Array<{
      fileName: string;
      status: FileSyncStatusString;
      isLocal: boolean;
    }>;
    syncStatus?: {
      syncing: boolean;
      progress: number;
      error?: string;
    };
    error?: string;
  }> {
    try {
      const result = await this.sendMessage('get-sync-status', { projectId });
      // The result from sendMessage is the data object
      const data = result as {
        fileStatuses?: Array<{
          fileName: string;
          status: FileSyncStatusString;
          isLocal: boolean;
        }>;
        syncStatus?: {
          syncing: boolean;
          progress: number;
          error?: string;
        };
      };
      return {
        success: true,
        fileStatuses: data.fileStatuses,
        syncStatus: data.syncStatus,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public async requestSyncCheck(
    projectId: string,
    projectPath: string,
    cloudFiles: Array<{
      _id: string;
      fileName: string;
      quickHash: string;
      fullHash: string;
      uploadedAt: string;
      size: number;
      contentType: string;
      s3Key: string;
    }>
  ): Promise<{
    success: boolean;
    fileStatuses?: Array<{
      fileName: string;
      status: FileSyncStatusString;
      isLocal: boolean;
    }>;
    error?: string;
  }> {
    try {
      const result = await this.sendMessage('request-sync-check', {
        projectId,
        projectPath,
        cloudFiles,
      });

      // The result from sendMessage is the data object, which contains fileStatuses
      const data = result as {
        fileStatuses?: Array<{
          fileName: string;
          status: FileSyncStatusString;
          isLocal: boolean;
        }>;
      };

      return {
        success: true,
        fileStatuses: data.fileStatuses,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public async getSyncProgress(projectId: string): Promise<{
    success: boolean;
    syncProgress?: {
      operation: SyncOperationString;
      fileName: string;
      progress: number;
    } | null;
    error?: string;
  }> {
    try {
      const result = await this.sendMessage('get-sync-progress', { projectId });
      // The result from sendMessage is the data object
      const data = result as {
        syncProgress?: {
          operation: SyncOperationString;
          fileName: string;
          progress: number;
        } | null;
      };
      return {
        success: true,
        syncProgress: data.syncProgress,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public async deleteFile(filePath: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const result = await this.sendMessage('delete-file', { filePath });
      // The result from sendMessage is the data object
      const data = result as { success?: boolean; error?: string };
      return {
        success: data.success ?? true,
        error: data.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public async openFile(filePath: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const result = await this.sendMessage('open-file', { filePath });
      // The result from sendMessage is the data object
      const data = result as { success?: boolean; error?: string };
      return {
        success: data.success ?? true,
        error: data.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public async getStoredProjectDirectories(): Promise<{
    success: boolean;
    projectDirectories?: Record<
      string,
      {
        projectId: string;
        directoryName: string;
        lastUsed: number;
        filePath?: string;
      }
    >;
    error?: string;
  }> {
    try {
      const result = await this.sendMessage(
        'get-stored-project-directories',
        {}
      );
      // The result from sendMessage is the data object
      const data = result as {
        success?: boolean;
        projectDirectories?: Record<
          string,
          {
            projectId: string;
            directoryName: string;
            lastUsed: number;
            filePath?: string;
          }
        >;
      };
      return {
        success: true,
        projectDirectories: data.projectDirectories,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  public destroy() {
    if (typeof window !== 'undefined' && this.isInitialized) {
      window.removeEventListener('message', this.boundMessageHandler);
      window.removeEventListener('focus', this.boundFocusHandler);
      this.isInitialized = false;
    }
    for (const { reject } of this.messageQueue.values()) {
      reject(new Error('Desktop bridge destroyed'));
    }
    this.messageQueue.clear();
  }
}

// Create singleton instance
export const desktopBridge = new DesktopBridge();
